import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import { CitationStyle, PageBodyPreviewResponse } from '../../types';
import { CITATION_STYLES, previewNotice } from './publishedBody';

/**
 * The article body as it will be PUBLISHED.
 *
 * WHY THIS IS NOT THE "Rendered" TAB. That tab renders `version.content` in the
 * browser — the reader-facing copy, with citations already stripped at build
 * time. It answers "what did the writer produce". This answers "what will the
 * deploy commit", which is a different question the moment sources are turned
 * on, and it answers it by asking the server to run the SAME builder both
 * deployers run (`services/deployers/body.py`). A preview with its own renderer
 * can be confidently wrong about the one thing it is for.
 *
 * The style selector re-renders without saving anything. Where a page's sources
 * actually come from is a property of the SITE, so committing it belongs in the
 * site's settings and this says so rather than writing it from here.
 */
export default function PublishedBodyPreview({
  pageId,
  versionId,
}: {
  pageId: number;
  versionId: number;
}) {
  const [style, setStyle] = useState<CitationStyle | null>(null);
  const [result, setResult] = useState<PageBodyPreviewResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (want: CitationStyle | null) => {
      setLoading(true);
      try {
        // `undefined` asks for the site's own setting, which is what the first
        // load wants: the honest answer before anyone has touched the control.
        const res = await api.previewPageBody(pageId, versionId, want ?? undefined);
        setResult(res);
        setError('');
      } catch (e: any) {
        setError(e?.message || 'Could not render the published view.');
      } finally {
        setLoading(false);
      }
    },
    [pageId, versionId],
  );

  useEffect(() => {
    load(style);
  }, [load, style]);

  const notice = result
    ? previewNotice({
        hasCitations: result.has_citations,
        style: result.citation_style,
        siteStyle: result.site_citation_style,
        siteType: result.site_type,
      })
    : null;

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-xs text-gray-500 dark:text-gray-400">Sources:</span>
        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {CITATION_STYLES.map((option) => {
            const active = (result?.citation_style ?? style) === option.value;
            return (
              <button
                key={option.value}
                type="button"
                title={option.hint}
                disabled={loading || (!!result && !result.has_citations)}
                onClick={() => setStyle(option.value)}
                className={`px-2.5 py-1 text-xs disabled:opacity-40 ${
                  active
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {loading && <span className="text-xs text-gray-400">Rendering…</span>}
      </div>

      {notice && (
        <p
          className={`mb-3 text-[11px] rounded px-2 py-1.5 ${
            notice.tone === 'warn'
              ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300'
              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
          }`}
        >
          {notice.text}
        </p>
      )}

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : (
        // The server rendered this from OUR markdown with OUR extension list —
        // the same string the deployer hands to GitHub or posts to WordPress.
        // Showing it any other way would be a third renderer, which is the
        // problem this whole path exists to remove.
        <div
          className="prose dark:prose-invert prose-sm max-w-none overflow-x-auto break-words"
          dangerouslySetInnerHTML={{ __html: result?.html || '' }}
        />
      )}
    </div>
  );
}
